/**
 * Merges server seat data into local seat state.
 *
 * Matching strategy (in priority order):
 *  1. serverSeatNumber already set  → match by server seat number (rotation-safe).
 *  2. playerId already set          → match by player ID.
 *  3. Blank placeholder seats (first ever merge):
 *       Build a stable index→serverPlayer assignment that GUARANTEES the local
 *       player (myPlayerId) lands at display index 0 (rotateSeats will then
 *       move it to Math.floor(total/2)).  All other players fill the remaining
 *       display slots in ascending serverSeatNumber order.
 *
 * Why this matters: the old code used `sorted[index]` which is purely
 * positional and would assign whichever server player happened to share an
 * index with the blank display slot — completely ignoring myPlayerId.
 * That caused the local player to appear at the wrong seat on first join,
 * making rotateSeats() unable to fix it because isMe was already wrong.
 */
export function mergeServerWithSeats(localSeats, serverPlayers, myPlayerId) {
    // ── Pre-build assignment map for blank-seat case ──────────────────────
    // Only computed once (not per-seat) so every seat in the blank branch
    // uses the same stable mapping.
    let blankAssignment = null
    const needsBlankAssignment = localSeats.some(
        s => s.serverSeatNumber == null && !s.playerId
    )
    if (needsBlankAssignment) {
        // Pin local player to display index 0; others fill remaining slots
        // in ascending serverSeatNumber order so the ordering is deterministic.
        const myServerPlayer = serverPlayers.find(p => p.playerId === myPlayerId)
        const others = [...serverPlayers]
            .filter(p => p.playerId !== myPlayerId)
            .sort((a, b) => a.seatNumber - b.seatNumber)

        // Build ordered list: [me, ...others]
        // rotateSeats() will later shift "me" to Math.floor(total/2).
        const ordered = myServerPlayer ? [myServerPlayer, ...others] : [...others]
        blankAssignment = ordered  // blankAssignment[displayIndex] = serverPlayer
    }

    return localSeats.map((seat, index) => {
        let serverPlayer

        if (seat.serverSeatNumber != null) {
            // Post-rotation merge: match by stored server seat number
            serverPlayer = serverPlayers.find(p => p.seatNumber === seat.serverSeatNumber)
        } else if (seat.playerId) {
            // Has playerId but no serverSeatNumber — match by playerId
            serverPlayer = serverPlayers.find(p => p.playerId === seat.playerId)
        } else {
            // Blank placeholder: use pre-built assignment
            serverPlayer = blankAssignment?.[index] ?? null
        }

        if (!serverPlayer) return seat

        return {
            ...seat,
            serverSeatNumber: serverPlayer.seatNumber,
            playerId: serverPlayer.playerId,
            name: serverPlayer.playerId,
            isMe: serverPlayer.playerId === myPlayerId,
            dealer: serverPlayer.dealer,
            points: seat.points   // preserve accumulated points across hands
        }
    })
}

/**
 * Rotates the seats array so that the local player (isMe) always appears
 * at the "bottom" position (index = Math.floor(total / 2)).
 */
export function rotateSeats(seats) {
    const total = seats.length
    const myIndex = seats.findIndex(s => s.isMe)

    if (myIndex === -1) return seats

    const fixedViewIndex = Math.floor(total / 2)
    const shift = myIndex - fixedViewIndex

    return seats.map((_, i) => {
        const sourceIndex = ((i + shift) % total + total) % total
        return seats[sourceIndex]
    })
}
/**
 * verifyRotation
 * ──────────────
 * Returns true if the local player (isMe) is already sitting at the canonical
 * "bottom" display index (Math.floor(total / 2)).
 *
 * Call this inside handleDealTrucoCards after seatRefs are ready.
 * If it returns false the seats were not rotated correctly (race or first-game
 * edge case) and you should dispatch SET_SEATS with the corrected array before
 * starting the deal animation.
 */
export function verifyRotation(seats) {
    const total = seats.length
    if (total === 0) return false
    const expectedIndex = Math.floor(total / 2)
    return seats[expectedIndex]?.isMe === true
}

/**
 * forceCorrectRotation
 * ─────────────────────
 * Re-runs rotateSeats on the current seats array and returns the corrected
 * array.  Use this when verifyRotation() returns false so that the deal
 * animation always starts with the correct layout.
 */
export function forceCorrectRotation(seats) {
    return rotateSeats(seats)
}